import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CONTRACT_MANAGED } from '../services/contract.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private lastAnalyticsNoticeAt = 0;

  constructor(
    private router: Router,
    private toastr: NbToastrService,
    private authService: AuthService,
    // TranslateService erst bei Bedarf holen: sein Loader nutzt HttpClient,
    // und HttpClient braucht diesen Interceptor — direkt injiziert waere das
    // ein Zirkel.
    private injector: Injector
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const suppressErrorToast = request.headers.has('X-Suppress-Error-Toast');
    if (suppressErrorToast) {
      request = request.clone({
        headers: request.headers.delete('X-Suppress-Error-Toast'),
      });
    }

    // Sign-in and second-factor requests answer 4xx as part of their normal
    // conversation: a wrong password, "your code please", "that code was not
    // accepted", "too many attempts". Those forms render the reason inline,
    // so this interceptor stays out of the way entirely for them.
    const isAuthExchange =
      request.url.endsWith('/token') ||   // sign-in, NOT /token/refresh
      request.url.includes('/2fa/');

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error(err);

        if (err.status === 401) {
          // Cloud storage 401 = expired OAuth token, not a session issue.
          // Let the calling component handle it (e.g. show reconnect prompt).
          const isCloudRequest =
            request.url.includes('/integrations/cloud/') ||
            request.url.includes('/oauth/');

          if (!isCloudRequest && !isAuthExchange) {
            this.router.navigate(['/auth/login']);
            this.authService.logout();
          }
          return throwError(() => err);
        }

        if (err && err.status >= 400 && !suppressErrorToast && !isAuthExchange) {
          if (request.url.includes('.json')) {
            // dont error out on json requests
            return throwError(() => err);
          }

          if (this.isHandledMoodleSiteStateError(err)) {
            return throwError(() => err);
          }

          // Individueller Vertrag: Stripe-Wege (Kauf, Planwechsel, Trial,
          // Kuendigung, Credit-Kauf) sind gesperrt. Statt „409 Error“ mit
          // dem Code ein verstaendlicher Hinweis.
          if (err.status === 409 && err?.error?.detail === CONTRACT_MANAGED) {
            const t = this.injector.get(TranslateService);
            this.toastr.info(
              t.instant('errors.contractManaged.message'),
              t.instant('errors.contractManaged.title')
            );
            return throwError(() => err);
          }

          // Analytics lehnt einen zu großen Abruf ab oder ist ausgelastet. Das
          // trifft alle Diagramme einer Seite zugleich: ein Hinweis genügt.
          const notice = this.analyticsNotice(err);
          if (notice) {
            if (Date.now() - this.lastAnalyticsNoticeAt > 10000) {
              this.lastAnalyticsNoticeAt = Date.now();
              const t = this.injector.get(TranslateService);
              this.toastr.warning(
                t.instant(`errors.${notice}.message`),
                t.instant(`errors.${notice}.title`)
              );
            }
            return throwError(() => err);
          }

          this.toastr.danger(
            this.errorMessage(err),
            `${err.status} Error`
          );
        }

        return throwError(() => err);
      })
    );
  }

  private isHandledMoodleSiteStateError(err: HttpErrorResponse): boolean {
    const code = err?.error?.detail?.code;
    return err.status === 410 && (
      code === 'site_disconnected' ||
      code === 'site_superseded'
    );
  }

  private analyticsNotice(err: HttpErrorResponse): string | null {
    const code = err?.error?.detail?.code;
    if (err.status === 413 && code === 'analytics_range_too_large') {
      return 'analyticsRangeTooLarge';
    }
    if (err.status === 503 && code === 'analytics_busy') {
      return 'analyticsBusy';
    }
    return null;
  }

  private errorMessage(err: HttpErrorResponse): string {
    const detail = err?.error?.detail;
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message;
    }
    if (typeof detail === 'string') {
      return detail;
    }
    if (typeof err?.error?.message === 'string') {
      return err.error.message;
    }
    return err.statusText || 'Request failed';
  }
}
